<template>
  <div class="folder-files-item w-full flex items-center overflow-auto">
    <div class="flex items-center">
      <CFileThumbnail :item="safeItem" />
    </div>

    <div class="flex flex-col justify-center gap-2 pa-3 truncate w-full">
      <div class="font-size-[18px] font-700 tracking-[0.4px] truncate line-height-[1.2]">
        {{ getFileName() }}
        <template v-if="isSlideType(item.type)">
          - {{ t('canvasUI.collectionSelector.pageNumber', { number: item.slide.index + 1 }) }}
        </template>
      </div>

      <div class="truncate line-height-[1.2]" :style="{ color: themeVars.text3 }">
        <component :is="renderContentType(item.file.content_type)" v-if="item.file?.content_type" class="font-size-3" />
      </div>

      <CTableTag
        v-if="files[getFileId()]?.tags?.length"
        display-as-text
        :maxTagsToShow="0"
        :modelValue="files[getFileId()]?.tags ?? []"
        :readonly="true"
        state="view"
      />

      <!--      Metadata disabled for now-->
      <!--      <div class="flex flex-wrap gap-2">-->
      <!--        <div class="flex flex-wrap gap-2">-->
      <!--          <NTag-->
      <!--            v-for="key in Object.keys(files[item.file.id]?.metadata ?? [])"-->
      <!--            :key="key"-->
      <!--            class="rounded-xl bg-primary6"-->
      <!--            size="small"-->
      <!--            :style="{-->
      <!--              '&#45;&#45;n-color': themeVars.primary6,-->
      <!--              '&#45;&#45;n-border': 'none',-->
      <!--              '&#45;&#45;n-text-color': themeVars.primary,-->
      <!--            }"-->
      <!--          >-->
      <!--            {{ key }}:-->
      <!--            {{-->
      <!--              files[item.file.id]?.metadata?.[key] instanceof Array-->
      <!--                ? files[item.file.id]?.metadata?.[key].join(', ')-->
      <!--                : files[item.file.id]?.metadata?.[key]-->
      <!--            }}-->
      <!--          </NTag>-->
      <!--        </div>-->
      <!--      </div>-->

      <div class="flex gap-2">
        <CInfoBadge
          v-if="item.file?.permissions?.can_share === false"
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="solid-share-from-square-slash"
          :text="t('canvasUI.files.notShareable')"
          variation="extended"
        />

        <CPopularityAndEngagementExtended
          class="flex gap-2 mr-2"
          :engagement="
            launchDarkly.show_popularity_icons ? popularityDict[files[getFileId()]?.id]?.engagement : undefined
          "
          :is-recommended="recommendedSectionIds.includes(files[getFileId()]?.id)"
          :popularity="
            launchDarkly.show_popularity_icons ? popularityDict[files[getFileId()]?.id]?.popularity : undefined
          "
          variation="extended"
          :views="launchDarkly.show_popularity_icons ? (popularityDict[files[getFileId()]?.id]?.views ?? 0) : 0"
        />
      </div>
    </div>

    <CDropdown
      :options="getActionsMenu()"
      :themeOverrides="dropdownThemeOverrides"
      trigger="click"
      @select="handleActions"
    >
      <CButton :circle="true">
        <CIcon icon="ellipsis" size="16px" />
      </CButton>
    </CDropdown>
  </div>
</template>

<script lang="tsx" setup>
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import useThemeVars from '@lib/composables/useThemeVars'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import type { RenderableFile } from '@lib/types/files'
import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { CollectionSlide } from '@collection-player/types/collection-player.type'
import { dropdownThemeOverrides } from '@collection-selector/utils/theme-overrides.util'
import type { ValuesOf } from '@lib/types/common'
import { useAppStore } from '@collection-selector/stores/app.store'

enum ReorderMenu {
  MoveUp = 'moveUp',
  MoveDown = 'moveDown',
  MoveToGroup = 'moveToGroup',
}

const props = defineProps<{
  item: CollectionSlide
  isFirst?: boolean
  isLast?: boolean
  disabledMoveToGroup?: boolean
}>()

const emit = defineEmits<{
  moveUp: []
  moveDown: []
  moveToGroup: []
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()
const themeVars = useThemeVars()
const { usedFiles: files } = useAppStore()
const { popularityDict } = useSectionPopularity()
const { recommendedSectionIds } = useRecommendations()
const { renderContentType } = useFileDisplayHelpers()

const isSlideType = (type: string) => type === 'slide'

const getFileId = () => {
  // Handle both V1 format (props.item.file.id) and V2 format (props.item.file_id)
  return props.item.file?.id || (props.item as any).file_id
}

const getFileName = () => {
  const fileId = getFileId()
  return files.value?.[fileId]?.name || props.item.file?.name || ''
}

const safeItem = computed(() => {
  if (isSlideType(props.item.type)) {
    const slideItem = props.item as any // Type assertion for slide access
    return {
      thumbnail_url: slideItem.slide?.url || null,
      content_type: props.item.file?.content_type || null,
      name: getFileName(),
    } as RenderableFile
  } else {
    return (props.item.file || {
      thumbnail_url: null,
      content_type: null,
      name: '',
    }) as unknown as RenderableFile
  }
})

function handleActions(key: ValuesOf<ReorderMenu>) {
  switch (key) {
    case ReorderMenu.MoveUp:
      emit(ReorderMenu.MoveUp)
      break
    case ReorderMenu.MoveDown:
      emit(ReorderMenu.MoveDown)
      break
    case ReorderMenu.MoveToGroup:
      emit(ReorderMenu.MoveToGroup)
      break
    default:
      break
  }
}

function getActionsMenu() {
  return [
    {
      disabled: props.isFirst,
      label: () => <div class="font-size-4">{t('canvasUI.collectionSelector.moveItem.moveUp')}</div>,
      key: ReorderMenu.MoveUp,
      icon: () => <CIcon icon="arrow-up" />,
    },
    {
      disabled: props.isLast,
      label: () => <div class="font-size-4">{t('canvasUI.collectionSelector.moveItem.moveDown')}</div>,
      key: ReorderMenu.MoveDown,
      icon: () => <CIcon icon="arrow-down" />,
    },
    {
      disabled: props.disabledMoveToGroup,
      label: () => <div class="font-size-4">{t('canvasUI.collectionSelector.moveItem.moveToGroup')}</div>,
      key: ReorderMenu.MoveToGroup,
      icon: () => <CIcon icon="arrows-from-dotted-line" />,
    },
  ]
}
</script>
